import Box from '@mui/material/Box';
import type { OptionsMap } from '@openx/types/options';
import { AllowBlockType } from '@openx/types/targeting/targeting';
import { Criteria, type CriteriaValue } from '@openx/types/targeting/targetingValuesTypes';
import { t } from 'i18next';
import type { JSX } from 'react';
import { DimensionChip } from '../../../shared/DimensionChip/DimensionChip';
import { Item, type ItemProps } from '../../../shared/SelectedItems/Item';
import { SelectedItems } from '../../../shared/SelectedItems/SelectedItems';
import { Switch } from '../../../shared/Switch/Switch';

type MetacategoryAllowBlockItemProps = {
  options?: OptionsMap;
  op?: CriteriaValue;
  handleChange: (values: string[], type: AllowBlockType, op?: CriteriaValue) => void;
  includes: string[];
  excludes: string[];
  readonly?: boolean;
  dataTest: string;
};

type SwitchComponentProps = {
  handleChange: (values: string[], type: AllowBlockType, op?: CriteriaValue) => void;
  includes: string[];
  switchState: boolean;
};

const SwitchComponent = ({ switchState, handleChange, includes }: SwitchComponentProps) => (
  <Switch
    switchState={switchState}
    onSwitchChange={() => handleChange(includes, AllowBlockType.ALLOW, switchState ? Criteria.ALL : Criteria.ANY)}
    switchOffLabel={t('all')}
    switchOnLabel={t('any')}
    groupTitle={t('of the following:')}
    customSx={{ width: '100%' }}
  />
);

const ItemComponent = (props: { item: ItemProps<string | JSX.Element>; op: CriteriaValue }) => (
  <Box
    sx={{
      '&:last-child': {
        '[data-test="dimension-false"]': {
          display: 'none',
        },
      },
      display: 'flex',
      flexDirection: 'row',
    }}
  >
    <Item {...props.item} />
    <DimensionChip targetingDimension={props.op} sx={{ marginLeft: 0, marginRight: 1 }} />
  </Box>
);

export const MetacategoryAllowBlockItem = ({
  options,
  op,
  handleChange,
  includes,
  excludes,
  readonly,
  dataTest,
}: MetacategoryAllowBlockItemProps) => {
  const hasAllowedItem = includes?.length !== 0;
  const hasBlockedItem = excludes?.length !== 0;
  const hasBothItems = hasAllowedItem && hasBlockedItem;

  const switchState = op === Criteria.ANY;

  return (
    <>
      {hasAllowedItem && (
        <SelectedItems
          filtersTitle={t('Allowed')}
          isRemovingAll
          isAddIcon={true}
          selectedItems={includes}
          readonly={readonly}
          handleChange={values => handleChange(values, AllowBlockType.ALLOW)}
          options={options}
          dataTest={`metacategory-allow-${dataTest}`}
          opSwitch={op && <SwitchComponent switchState={switchState} handleChange={handleChange} includes={includes} />}
          ItemComponent={item => (op ? <ItemComponent item={item} op={op} /> : <Item {...item} />)}
        />
      )}

      {hasBothItems && <DimensionChip targetingDimension={Criteria.ALL} isInsideOption={true} />}

      {hasBlockedItem && (
        <SelectedItems
          filtersTitle={t('Blocked')}
          isRemovingAll
          isAddIcon={false}
          selectedItems={excludes}
          readonly={readonly}
          handleChange={values => handleChange(values, AllowBlockType.BLOCK)}
          options={options}
          dataTest={`metacategory-block-${dataTest}`}
        />
      )}
    </>
  );
};
